import { computed, ref } from 'vue';

import type { MessageInfoSchemaType } from '@/entities/message';

import { useMessageCreateAttachmentsState } from './create-attachments-state';

const useMessageEditAttachmentsState = () => {
  const initialAttachments = ref<MessageInfoSchemaType['attachments']>([]);
  const removedAttachmentIds = ref<string[]>([]);

  const {
    attachments: selectedAttachments,
    files,
    isAttachmentsExists: isSelectedAttachmentsExists,
    addFiles,
    removeAttachment,
    resetAttachments: resetSelectedAttachments,
  } = useMessageCreateAttachmentsState();

  const allAttachments = computed<MessageInfoSchemaType['attachments']>(
    () => initialAttachments.value,
  );
  const existingAttachments = computed<MessageInfoSchemaType['attachments']>(() =>
    allAttachments.value.filter(({ id }) => !removedAttachmentIds.value.includes(id)),
  );

  const deleteAttachmentIds = computed<string[]>(() => removedAttachmentIds.value);
  const isAttachmentsExists = computed<boolean>(
    () => existingAttachments.value.length > 0 || isSelectedAttachmentsExists.value,
  );
  const isAttachmentsChanged = computed<boolean>(
    () => removedAttachmentIds.value.length > 0 || isSelectedAttachmentsExists.value,
  );

  const setCurrentAttachments = (attachments: MessageInfoSchemaType['attachments']) => {
    initialAttachments.value = attachments;
    removedAttachmentIds.value = [];
    resetSelectedAttachments();
  };

  const removeExistingAttachment = (
    attachmentId: MessageInfoSchemaType['attachments'][number]['id'],
  ) => {
    if (!removedAttachmentIds.value.includes(attachmentId)) {
      removedAttachmentIds.value.push(attachmentId);
    }
  };

  const restoreExistingAttachment = (
    attachmentId: MessageInfoSchemaType['attachments'][number]['id'],
  ) => {
    removedAttachmentIds.value = removedAttachmentIds.value.filter((id) => id !== attachmentId);
  };

  const isAttachmentRemoved = (attachmentId: MessageInfoSchemaType['attachments'][number]['id']) =>
    removedAttachmentIds.value.includes(attachmentId);

  const resetAttachments = () => {
    removedAttachmentIds.value = [];
    resetSelectedAttachments();
  };

  return {
    allAttachments,
    existingAttachments,
    selectedAttachments,
    files,
    addFiles,
    removeAttachment,
    deleteAttachmentIds,
    isAttachmentsExists,
    isAttachmentsChanged,
    setCurrentAttachments,
    removeExistingAttachment,
    restoreExistingAttachment,
    isAttachmentRemoved,
    resetAttachments,
  };
};

export { useMessageEditAttachmentsState };
